import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { useMenuPortal } from './useMenuPortal';

type AcademiaDateFieldProps = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
  id?: string;
  label?: string;
  /** Masque le libellé au-dessus du champ (ex. grille notes) */
  hideLabel?: boolean;
  /** Date ISO minimale (inclusive) — ex. aujourd’hui pour autoriser uniquement le futur. */
  minDate?: string;
  /** Date ISO maximale (inclusive) — ex. aujourd’hui pour bloquer les dates futures. */
  maxDate?: string;
};

const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'] as const;
const MONTHS_FR = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
] as const;

function joinClasses(...parts: (string | undefined | false)[]) {
  return parts.filter(Boolean).join(' ');
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseIsoDate(iso: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDisplayFr(iso: string): string {
  const d = parseIsoDate(iso);
  if (!d) return 'Choisir une date';
  return d.toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1, 12);
}

function addMonths(d: Date, delta: number) {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1, 12);
}

function mondayBasedWeekday(d: Date) {
  return (d.getDay() + 6) % 7;
}

export default function AcademiaDateField({
  value,
  onChange,
  className,
  disabled,
  id: idProp,
  label = 'Date de séance',
  hideLabel = false,
  minDate,
  maxDate,
}: AcademiaDateFieldProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  const selected = parseIsoDate(value) ?? new Date();
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(selected));

  useEffect(() => {
    if (value) setViewMonth(startOfMonth(parseIsoDate(value) ?? new Date()));
  }, [value]);

  const { menuRef, style } = useMenuPortal(open, () => setOpen(false), anchorRef);

  const todayIso = toIsoDate(new Date());

  const calendarCells = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const first = new Date(year, month, 1, 12);
    const leading = mondayBasedWeekday(first);
    const daysInMonth = new Date(year, month + 1, 0, 12).getDate();
    const cells: { iso: string; day: number; inMonth: boolean }[] = [];

    for (let i = 0; i < leading; i += 1) {
      const d = new Date(year, month, -leading + i + 1, 12);
      cells.push({ iso: toIsoDate(d), day: d.getDate(), inMonth: false });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const d = new Date(year, month, day, 12);
      cells.push({ iso: toIsoDate(d), day, inMonth: true });
    }
    while (cells.length % 7 !== 0 || cells.length < 42) {
      const last = cells[cells.length - 1];
      const d = parseIsoDate(last.iso)!;
      const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 12);
      cells.push({ iso: toIsoDate(next), day: next.getDate(), inMonth: false });
    }
    return cells.slice(0, 42);
  }, [viewMonth]);

  const pick = (iso: string) => {
    onChange(iso);
    setOpen(false);
  };

  const panel =
    open &&
    createPortal(
      <div
        ref={menuRef}
        role="dialog"
        aria-label="Choisir une date"
        style={{ ...style, width: Math.max(Number(style.width) || 0, 296) }}
        className="academia-date-panel"
      >
        <div className="flex items-center justify-between gap-2 border-b border-outline-variant/15 px-3 py-2">
          <button
            type="button"
            className="academia-date-nav"
            aria-label="Mois précédent"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setViewMonth((m) => addMonths(m, -1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <p className="text-sm font-bold text-primary">
            {MONTHS_FR[viewMonth.getMonth()]} {viewMonth.getFullYear()}
          </p>
          <button
            type="button"
            className="academia-date-nav"
            aria-label="Mois suivant"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setViewMonth((m) => addMonths(m, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-0.5 px-2 pt-2 text-center">
          {WEEKDAYS.map((w, i) => (
            <span
              key={`${w}-${i}`}
              className="py-1 text-[10px] font-bold uppercase text-on-surface-variant"
            >
              {w}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-0.5 p-2">
          {calendarCells.map((cell) => {
            const isSelected = cell.iso === value;
            const isToday = cell.iso === todayIso;
            const isBeforeMin = Boolean(minDate && cell.iso < minDate);
            const isAfterMax = Boolean(maxDate && cell.iso > maxDate);
            const isDisabledDay = isBeforeMin || isAfterMax;
            return (
              <button
                key={cell.iso}
                type="button"
                disabled={!cell.inMonth || isDisabledDay}
                className={joinClasses(
                  'academia-date-day',
                  !cell.inMonth && 'academia-date-day--muted',
                  isDisabledDay && 'academia-date-day--disabled',
                  isToday && !isSelected && 'academia-date-day--today',
                  isSelected && 'academia-date-day--selected',
                )}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (cell.inMonth && !isDisabledDay) pick(cell.iso);
                }}
              >
                {cell.day}
              </button>
            );
          })}
        </div>

        <div className="border-t border-outline-variant/15 px-3 py-2">
          <button
            type="button"
            disabled={Boolean(minDate && todayIso < minDate) || Boolean(maxDate && todayIso > maxDate)}
            className="w-full rounded-lg bg-primary px-3 py-2 text-xs font-bold text-white hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pick(todayIso)}
          >
            Aujourd&apos;hui
          </button>
        </div>
      </div>,
      document.body,
    );

  return (
    <div ref={anchorRef} className={joinClasses('relative w-full', hideLabel ? '' : 'max-w-sm', className)}>
      {!hideLabel && label ? (
        <label
          htmlFor={id}
          className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-on-surface-variant"
        >
          {label}
        </label>
      ) : null}
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={joinClasses('academia-date-trigger', open && 'academia-date-trigger--open')}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
      >
        <Calendar className="h-4 w-4 shrink-0 text-primary" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-left">{formatDisplayFr(value)}</span>
      </button>
      {panel}
    </div>
  );
}
